import type { ProjectsV2EditProjectData } from '@civilgrid/rest-client';
import {
  Badge,
  Button,
  DatePicker,
  datePickerValueToDateString,
  ExpandableText,
  Icon,
  Label,
  Select,
  stringToDatePickerValue,
  TextArea,
  Tooltip,
} from '@civilgrid/ui/components';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import type { DateTime } from 'luxon';
import { type FormEvent, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';
import authorizations, { Action } from '~/authorizations';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { planStatusLabels } from '~/common/projectUtils';
import type { IntelligenceSummary, PlanStatus, Project } from '~/common/types';
import { Notify } from '~/components2/Notify';
import { projectIntelligenceQueryKey } from '~/hooks/useProjectIntelligence';
import { useUpdateIntelligenceSummary } from '~/hooks/useUpdateIntelligenceSummary';
import { useUpdateProject } from '~/hooks/useUpdateProject';
import rootStore from '~/store';
import { buildRecommendationDetail, recommendationChipVariant, recommendationInfo } from './recommendation';

const YEAR_RANGE = { start: 2020, end: 2040 };

const planStatusOptions = Object.entries(planStatusLabels).map(([value, label]) => ({ value, label }));

type OverviewDraft = { planStatus?: PlanStatus; startDate: string; endDate: string; notes: string };

function formatDate(date: DateTime | undefined) {
  return date?.isValid ? date.toFormat('MM/dd/yyyy') : '-';
}

function errorReason(e: unknown) {
  return (e as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
}

function OverviewField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 text-xs">
      <dt className="text-grey-60">{label}</dt>
      <dd className="font-medium text-grey-90">{children}</dd>
    </div>
  );
}

/** Plan status, construction dates, recommendation and notes for the project, editable in place. */
export default function ProjectOverview({
  project,
  summary,
}: {
  project: Project;
  summary: IntelligenceSummary | null | undefined;
}) {
  const queryClient = useQueryClient();
  const fieldIdPrefix = useId();

  useSnapshot(rootStore);
  const canEdit = authorizations.can(Action.EditProjectDetails);

  const notes = summary?.notes ?? '';
  const recommendation = summary?.schedule?.recommendation;
  const startIso = project.constructionStartDate?.toISODate() ?? '';
  const endIso = project.constructionEndDate?.toISODate() ?? '';

  const [draft, setDraft] = useState<OverviewDraft | null>(null);
  // Saved values the draft is compared against; advanced per successful request so a retry never resends it.
  const [baseline, setBaseline] = useState<OverviewDraft | null>(null);
  const isEditing = draft !== null;
  const editButtonRef = useRef<HTMLButtonElement>(null);
  const planStatusRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);

  // Moves focus into the form on Edit and back to the Edit button once the form closes.
  useEffect(() => {
    if (isEditing && !wasEditing.current) planStatusRef.current?.focus();
    if (!isEditing && wasEditing.current) editButtonRef.current?.focus();
    wasEditing.current = isEditing;
  }, [isEditing]);

  const analyticsProps = { project_id: project.id, organization_id: project.organization };

  const updateProject = useUpdateProject({
    onSuccess: (_data, { body }) => {
      if (body?.planStatus) {
        capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2PlanStatusChanged, {
          ...analyticsProps,
          plan_status: body.planStatus,
        });
      }
      if (body && 'constructionStartDate' in body) {
        capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2ConstructionDatesChanged, {
          ...analyticsProps,
          old_start_date: startIso || null,
          old_end_date: endIso || null,
          new_start_date: body.constructionStartDate,
          new_end_date: body.constructionEndDate,
        });
      }
    },
  });

  const updateSummary = useUpdateIntelligenceSummary({
    onSuccess: (_data, { body }) => {
      const savedNotes = body?.properties.notes;
      capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2NotesUpdated, {
        ...analyticsProps,
        notes_length: typeof savedNotes === 'string' ? savedNotes.length : 0,
      });
    },
  });

  const recommendationDetail = useMemo(
    () =>
      recommendation &&
      buildRecommendationDetail(
        recommendation,
        summary?.schedule?.conflictsStart,
        summary?.schedule?.conflictsEnd,
        project.constructionStartDate,
        project.constructionEndDate,
      ),
    [
      recommendation,
      summary?.schedule?.conflictsStart,
      summary?.schedule?.conflictsEnd,
      project.constructionStartDate,
      project.constructionEndDate,
    ],
  );

  const planStatusChanged = !!draft && !!baseline && draft.planStatus !== baseline.planStatus;
  const datesChanged =
    !!draft && !!baseline && (draft.startDate !== baseline.startDate || draft.endDate !== baseline.endDate);
  const notesChanged = !!draft && !!baseline && draft.notes !== baseline.notes;
  const datesInvalid = !!draft && !!draft.startDate && !!draft.endDate && draft.startDate > draft.endDate;
  const isSaving = updateProject.isPending || updateSummary.isPending;
  const canSave = (planStatusChanged || datesChanged || notesChanged) && !datesInvalid && !isSaving;

  const startEditing = () => {
    const saved = { planStatus: project.planStatus, startDate: startIso, endDate: endIso, notes };
    setBaseline(saved);
    setDraft(saved);
    capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2OverviewEditStarted, analyticsProps);
  };

  const cancelEditing = () => {
    setDraft(null);
    setBaseline(null);
    capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2OverviewEditCancelled, analyticsProps);
  };

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!draft || !canSave) return;

    const attributes: NonNullable<ProjectsV2EditProjectData['body']> = {};
    if (planStatusChanged && draft.planStatus) attributes.planStatus = draft.planStatus;
    if (datesChanged) {
      attributes.constructionStartDate = draft.startDate;
      attributes.constructionEndDate = draft.endDate;
    }

    const saves: { label: string; saved: Partial<OverviewDraft>; request: Promise<unknown> }[] = [];
    if (Object.keys(attributes).length > 0) {
      const projectLabel = [planStatusChanged && 'plan status', datesChanged && 'construction dates']
        .filter(Boolean)
        .join(' and ');
      saves.push({
        label: projectLabel,
        saved: { planStatus: draft.planStatus, startDate: draft.startDate, endDate: draft.endDate },
        request: updateProject.mutateAsync({ path: { projectId: project.id }, body: attributes }),
      });
    }
    if (notesChanged && summary) {
      saves.push({
        label: 'notes',
        saved: { notes: draft.notes },
        request: updateSummary.mutateAsync({
          path: { summaryId: summary.id },
          body: { properties: { notes: draft.notes } },
        }),
      });
    }

    const results = await Promise.allSettled(saves.map((save) => save.request));
    queryClient.invalidateQueries({ queryKey: ['project', { projectId: project.id }] });
    queryClient.invalidateQueries({ queryKey: projectIntelligenceQueryKey(project.id) });

    const failure = results.find((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (!failure) {
      Notify.info({ message: 'Project intelligence details updated.' });
      setDraft(null);
      setBaseline(null);
      return;
    }

    const fulfilled = saves.filter((_, i) => results[i].status === 'fulfilled');
    setBaseline((current) => current && Object.assign({ ...current }, ...fulfilled.map((save) => save.saved)));
    const labelsWith = (status: PromiseSettledResult<unknown>['status']) =>
      saves.filter((_, i) => results[i].status === status).map((save) => save.label);
    const saved = labelsWith('fulfilled');
    const reason = errorReason(failure.reason) || 'An error occurred while saving.';
    Notify.error({
      message: saved.length
        ? `Saved ${saved.join(' and ')}, but couldn't save ${labelsWith('rejected').join(' and ')}. ${reason}`
        : reason,
    });
  };

  const renderRecommendation = (inDefinitionList: boolean) => {
    if (!recommendation) return null;
    const Term = inDefinitionList ? 'dt' : 'div';
    const Detail = inDefinitionList ? 'dd' : 'div';
    return (
      <div className="flex shrink-0 flex-col gap-1.5 text-xs" data-testid="intelligence-v2-overview-recommendation">
        <Term className="flex items-center gap-1">
          <span className={classNames(isEditing ? 'text-primary' : 'text-grey-60')}>Recommendation</span>
          <Tooltip
            aria-label="Recommendation details"
            side="bottom"
            className="max-w-72"
            triggerClassName="text-grey-60"
            data-testid="intelligence-recommendation-legend-trigger"
            contentChildren={
              <div className="flex flex-col gap-1 text-xs text-white">
                <span className="font-semibold">{recommendationDetail?.title}</span>
                <span>{recommendationDetail?.description}</span>
              </div>
            }
          >
            <Icon icon="questionMarkCircled" size={14} className={isEditing ? 'text-primary' : 'text-grey-60'} />
          </Tooltip>
        </Term>
        <Detail className="mt-1">
          <Badge color={recommendationChipVariant(recommendation)} data-testid="intelligence-recommendation-chip">
            {recommendationInfo[recommendation].label}
          </Badge>
        </Detail>
      </div>
    );
  };

  const ids = {
    planStatus: `${fieldIdPrefix}-plan-status`,
    start: `${fieldIdPrefix}-construction-start`,
    end: `${fieldIdPrefix}-construction-end`,
    notes: `${fieldIdPrefix}-notes`,
    datesError: `${fieldIdPrefix}-dates-error`,
  };

  const editForm = draft && (
    <form className="flex flex-col gap-3" onSubmit={handleSave} data-testid="intelligence-v2-overview-form">
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <Label htmlFor={ids.planStatus} className="text-xs">
            Plan Status
          </Label>
          <Select
            ref={planStatusRef}
            id={ids.planStatus}
            options={planStatusOptions}
            value={draft.planStatus}
            onValueChange={(next) => next && setDraft({ ...draft, planStatus: next as PlanStatus })}
            placeholder="Select an option"
            disabled={isSaving}
            data-testid="intelligence-plan-status-select"
          />
        </div>
        <DatePicker
          id={ids.start}
          label="Construction Start"
          className="w-auto min-w-0 flex-1"
          value={stringToDatePickerValue(draft.startDate)}
          onChange={(next) => setDraft({ ...draft, startDate: datePickerValueToDateString(next) || draft.startDate })}
          instantSelect
          allowClear={false}
          yearRange={YEAR_RANGE}
          placeholder="Select a date"
          disabled={isSaving}
          aria-invalid={datesInvalid}
          aria-describedby={datesInvalid ? ids.datesError : undefined}
          data-testid="intelligence-v2-overview-construction-start"
        />
        <DatePicker
          id={ids.end}
          label="Construction End"
          className="w-auto min-w-0 flex-1"
          value={stringToDatePickerValue(draft.endDate)}
          onChange={(next) => setDraft({ ...draft, endDate: datePickerValueToDateString(next) || draft.endDate })}
          instantSelect
          allowClear={false}
          yearRange={YEAR_RANGE}
          placeholder="Select a date"
          disabled={isSaving}
          aria-invalid={datesInvalid}
          aria-describedby={datesInvalid ? ids.datesError : undefined}
          data-testid="intelligence-v2-overview-construction-end"
        />
        {renderRecommendation(false)}
      </div>
      {datesInvalid && (
        <p
          id={ids.datesError}
          role="alert"
          className="text-xs text-red-50"
          data-testid="intelligence-v2-overview-dates-error"
        >
          Construction start must be on or before construction end.
        </p>
      )}
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={ids.notes} className="text-xs">
          Notes
        </Label>
        <TextArea
          id={ids.notes}
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
          placeholder="Enter a note"
          disabled={isSaving || !summary}
          className="min-h-12"
          data-testid="intelligence-v2-overview-notes-input"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          variant={canSave ? 'primary' : 'disabled'}
          disabled={!canSave}
          loading={isSaving}
          data-testid="intelligence-v2-overview-save"
        >
          <Icon icon="check" size={14} />
          Save
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={cancelEditing}
          disabled={isSaving}
          data-testid="intelligence-v2-overview-cancel"
        >
          <Icon icon="cross2" size={14} />
          Cancel
        </Button>
      </div>
    </form>
  );

  const readView = (
    <>
      <dl className="flex items-start justify-between gap-3" data-testid="intelligence-v2-overview-details">
        <OverviewField label="Plan Status">
          {project.planStatus ? planStatusLabels[project.planStatus] : '-'}
        </OverviewField>
        <OverviewField label="Construction Start">{formatDate(project.constructionStartDate)}</OverviewField>
        <OverviewField label="Construction End">{formatDate(project.constructionEndDate)}</OverviewField>
        {renderRecommendation(true) ?? <div />}
      </dl>
      {notes && (
        <dl className="flex flex-col gap-1 text-xs" data-testid="intelligence-v2-overview-notes">
          <dt className="text-grey-60">Notes</dt>
          <dd className="font-medium">
            <ExpandableText
              toggleTestId="intelligence-v2-overview-notes-toggle"
              onExpandedChange={(expanded) => {
                if (expanded) capturePosthogEvent(POSTHOG_EVENTS.intelligenceV2NotesExpanded, analyticsProps);
              }}
            >
              {notes}
            </ExpandableText>
          </dd>
        </dl>
      )}
      {canEdit && (
        <div>
          <Button
            ref={editButtonRef}
            type="button"
            variant="secondary"
            onClick={startEditing}
            data-testid="intelligence-v2-overview-edit"
          >
            <Icon icon="edit" size={14} />
            Edit
          </Button>
        </div>
      )}
    </>
  );

  return (
    <div className="flex flex-col gap-3 mb-3" data-testid="intelligence-v2-overview">
      {isEditing ? editForm : readView}
    </div>
  );
}
